<script setup lang="ts">
// One row of the overview: tag (its kind, kindTag, or a player's HDMI), dot, name, one short fact,
// timeline bar. Used by the node page (EntityList, live status from the
// store) and the fleet page (FleetView, state and events from central), so
// the two look and behave the same. `events`/`overlay` feed the bar when the
// store is not the source; `cfg` lets the bar's tooltip name entities.
import type { Config, Event } from '../api'
import { dotClass, kindTag } from '../util'
import TimelineBar from './TimelineBar.vue'

defineProps<{
    kind: 'input' | 'encode' | 'destination' | 'output' | 'machine' | 'node' | 'storage'
    id: string
    name: string
    tag?: string
    fact?: string
    extra?: string
    state: string
    nested?: boolean
    stacked?: boolean // name on one line, the bar across the whole row below it (fleet cards)
    selected?: boolean
    collapsible?: boolean
    collapsed?: boolean
    href?: string
    events?: Event[]
    overlay?: Event[]
    cfg?: Config
    loading?: boolean // its timeline's events are still coming
}>()
const emit = defineEmits<{ select: []; open: [{ from: number; to: number }]; toggle: [] }>()
</script>

<template>
    <component
        :is="href ? 'a' : 'div'"
        :href="href"
        class="entity-row chrome"
        :class="[
            stacked ? 'entity-row-stacked' : 'cursor-pointer',
            selected ? 'bg-accent/10 shadow-[inset_2px_0_0_var(--accent)]' : 'hover:bg-bg-2',
        ]"
        :tabindex="href ? undefined : 0"
        :role="href ? undefined : 'button'"
        :aria-pressed="href ? undefined : !!selected"
        @click="emit('select')"
        @keydown.enter.space.self.prevent="emit('select')"
    >
        <button
            v-if="collapsible"
            class="size-3.5 btn-ghost p-0 text-[11px]/3.5 text-fg-3"
            :aria-label="collapsed ? 'Expand' : 'Collapse'"
            @click.prevent.stop="emit('toggle')"
        >
            {{ collapsed ? '▸' : '▾' }}
        </button>
        <span v-else />
        <span :class="dotClass(state, nested)" role="img" :aria-label="state || 'unknown'" :title="state" />
        <div class="truncate" :class="{ 'pl-4.5': nested }">
            <span
                v-if="tag ?? kindTag(kind)"
                class="mr-1.25 inline-block min-w-8 rounded-[3px] bg-bg-3 px-1 text-center align-[1px] font-mono text-[10px] text-fg-2"
                >{{ tag ?? kindTag(kind) }}</span
            ><span v-if="name">{{ name }}</span> <span v-if="fact" class="text-fg-2 tabular-nums">{{ fact }}</span
            ><span v-if="extra" class="text-fg-3"> · {{ extra }}</span
            ><slot />
        </div>
        <TimelineBar
            :id="id"
            :class="{ 'col-span-full': stacked }"
            :kind="kind"
            :current="state"
            :events="events"
            :overlay="overlay"
            :cfg="cfg"
            :loading="loading"
            @open="emit('open', $event)"
        />
    </component>
</template>
